import type { SxProps, Theme } from '@mui/material';
import type { DateValidationError } from '@mui/x-date-pickers';
import type {
  Control,
  FieldErrors,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { Box, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { AddressesAutocomplete } from '@/components/AddressesAutocomplete';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { CurrencyDecimalInput, CurrencyFixedDecimalInput } from '@/shared/formatters';

interface Props {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  watch: UseFormWatch<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  isSmbInvites: boolean;
}

export const LeaseInformation = ({
  control,
  errors,
  watch,
  setValue,
  trigger,
  isSmbInvites,
}: Props) => {
  const leaseStart = watch('leaseStart');
  const { isFeatureFlagEnabled, isReady } = useSplitFeatureFlag(
    SplitFeatureFlag.MONARCH_446_SWAP_BUILDINGS_TO_PROPERTY_API,
  );
  const isNewPropertyApi = isReady && isFeatureFlagEnabled;
  const addressLabel = isNewPropertyApi
    ? 'Search by address or property name'
    : 'Building name or address';

  return (
    <>
      <Typography variant="h5" component="h2" mb={1}>
        Lease information
      </Typography>
      <Typography variant="body1" mb={3}>
        Enter the lease details below. If your renter is paying for the entire first month upfront
        at lease signing, we don’t need to cover it (optional).
      </Typography>
      <Controller
        name="address"
        control={control}
        render={({ field }) => {
          return (
            <AddressesAutocomplete
              required
              value={field.value?.monolith_building_id}
              onBlur={() => field.onBlur()}
              inputSx={overrideStylesForAutocomplete}
              onChange={(value) => field.onChange(value)}
              name={field.name}
              label={addressLabel}
              showLoadingSkeleton={true}
              error={!!errors.address}
              helperText={
                errors.address?.monolith_building_id
                  ? errors.address?.monolith_building_id.message?.toString() || ' '
                  : errors.address?.message?.toString() || ' '
              }
            />
          );
        }}
      />
      <Controller
        name="apartmentNumber"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            ref={null}
            id={'apartmentNumber'}
            label="Unit number"
            required
            error={!!errors.apartmentNumber}
            helperText={errors.apartmentNumber?.message?.toString() ?? ' '}
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: 4 }}>
        <Controller
          name="leaseStart"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              value={field.value ? dayjs(field.value) : null}
              label="Lease start date"
              views={['year', 'month', 'day']}
              openTo="month"
              maxDate={dayjs().add(1, 'year')}
              minDate={dayjs().subtract(1, 'year')}
              onChange={(value: dayjs.Dayjs | null) => {
                if (value?.isValid()) {
                  setValue('leaseStart', value.toDate(), { shouldValidate: true });
                  setValue('leaseEnd', value.add(1, 'year').subtract(1, 'day').toDate(), {
                    shouldValidate: true,
                  });
                }
              }}
              onError={(_error: DateValidationError, value: dayjs.Dayjs | null | any) => {
                setValue('leaseStart', value);
              }}
              shouldDisableDate={(value: dayjs.Dayjs) =>
                value.isAfter(
                  dayjs(new Date().setHours(23, 59, 59, 999))
                    .add(1, 'year')
                    .subtract(1, 'day')
                    .toDate(),
                ) ||
                value.isBefore(
                  dayjs(new Date().setHours(0, 0, 0, 0))
                    .subtract(1, 'year')
                    .add(1, 'day')
                    .toDate(),
                )
              }
              slots={{
                openPickerIcon: CalendarTodayIcon,
              }}
              slotProps={{
                desktopPaper: {
                  onBlur: () => trigger('leaseStart'),
                },
                textField: {
                  id: 'lease_start',
                  variant: 'filled',
                  required: true,
                  helperText: errors.leaseStart?.message?.toString() ?? 'MM/DD/YYYY',
                  error: !!errors.leaseStart,
                  onBlur: () => trigger('leaseStart'),
                  'aria-label': 'Start lease date - text field',
                },
                openPickerButton: {
                  'aria-label': 'Start lease date - open calendar',
                },
              }}
            />
          )}
        />
        <Controller
          name="leaseEnd"
          control={control}
          render={({ field }) => (
            <DatePicker
              {...field}
              value={field.value ? dayjs(field.value) : null}
              label="Lease end date"
              views={['year', 'month', 'day']}
              openTo="month"
              maxDate={dayjs(leaseStart).add(10, 'year').add(1, 'month')}
              minDate={dayjs(leaseStart).add(1, 'day')}
              onChange={async (value: dayjs.Dayjs | null) => {
                if (value?.isValid()) {
                  setValue('leaseEnd', value.toDate());
                }
              }}
              slots={{
                openPickerIcon: CalendarTodayIcon,
              }}
              onError={(_error: DateValidationError, value: dayjs.Dayjs | null | any) => {
                setValue('leaseEnd', value);
              }}
              slotProps={{
                desktopPaper: {
                  onBlur: () => trigger('leaseEnd'),
                },
                textField: {
                  id: 'lease_end',
                  variant: 'filled',
                  required: true,
                  helperText: errors.leaseEnd?.message?.toString() ?? 'MM/DD/YYYY',
                  error: !!errors.leaseEnd,
                  onBlur: () => trigger('leaseEnd'),
                  'aria-label': 'End lease date - text field',
                },
                openPickerButton: {
                  'aria-label': 'End lease date - open calendar',
                },
              }}
            />
          )}
        />
      </Stack>
      <Controller
        name="grossMonthlyRent"
        control={control}
        render={({ field }) => (
          <TextField
            {...field}
            ref={null}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputComponent: CurrencyDecimalInput,
              disableUnderline: true,
            }}
            label="Total monthly rent"
            id={'gross_monthly_rent'}
            required
            error={!!errors.grossMonthlyRent}
            helperText={
              errors.grossMonthlyRent?.message?.toString() ??
              'This is the market price rent that is on the lease.'
            }
          />
        )}
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 0, sm: 4 }}>
        <Controller
          name="prepaidRent"
          control={control}
          render={({ field }) => (
            <>
              <TextField
                {...field}
                ref={null}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputComponent: CurrencyFixedDecimalInput,
                  disableUnderline: true,
                }}
                label="Prepaid rent"
                id={'prepaid-rent'}
                error={!!errors.prepaidRent}
                helperText={
                  <>
                    <Box>{errors.prepaidRent?.message?.toString()}</Box>
                    <Typography variant="helpertext">
                      {isSmbInvites
                        ? `Any rent that will be collected at lease signing. Prepaid rent is not covered by the policy.`
                        : ''}
                    </Typography>
                  </>
                }
              />
            </>
          )}
        />
        <Controller
          name="freeRent"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              ref={null}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                inputComponent: CurrencyFixedDecimalInput,
                disableUnderline: true,
              }}
              label="Free rent"
              id={'free-rent'}
              error={!!errors.freeRent}
              helperText={
                <>
                  <Box>{errors.freeRent?.message?.toString()}</Box>
                  <Typography variant="helpertext">
                    {isSmbInvites
                      ? 'Any concessions offered to the renter where they are not responsible for making rent payments. Free rent is not covered by the policy.'
                      : ''}
                  </Typography>
                </>
              }
            />
          )}
        />
      </Stack>
    </>
  );
};

/**
 * These styles are used to match the old styles from the invite form,
 * because it doesn't use the component library. To get rid of this,
 * we need to migrate to the component library entirely
 */
const overrideStylesForAutocomplete: SxProps<Theme> = {
  '& .MuiFormLabel-root.Mui-error': {
    color: 'rgb(255, 48, 48) !important',
  },
  '& .MuiInputBase-root.Mui-error': {
    boxShadow: 'rgb(255, 48, 48) 0px 0px 0px 1px inset',
    backgroundColor: '#ECF7EE !important',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: 'rgb(255, 48, 48) !important',
  },
};
